import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useParams } from "@packages/framework";
import {
  type BibleBookId,
  useRequestBibleBookSaves,
  useRequestBibleSaveMutations,
} from "@packages/sdk";
import { IconButton } from "@packages/ui/src/components/_base/_core/IconButton/IconButton.emotion";
import { BookmarkIcon } from "@packages/ui/src/components/_base/icons/Icon/GeneralIcons";
import { useMemo } from "react";

import { bibleProperties } from "./analytics.utilities";

export const BibleBookmarkButton = ({
  translationId,
  book,
  chapter,
  title,
}: {
  book: BibleBookId;
  chapter: string | number;
  title: string;
  translationId: number;
}) => {
  const { translation } = useParams();
  const { data: savesResponse } = useRequestBibleBookSaves({
    translationId,
    bookId: book,
  });
  const bookmark = useMemo(
    () =>
      (savesResponse?.saves ?? []).find(
        (s) =>
          s.type === "bookmark" &&
          s.book_id === book &&
          s.chapter === parseInt(chapter.toString()),
      ),
    [savesResponse, book, chapter],
  );
  const { addBookmark, deleteBookmark } = useRequestBibleSaveMutations();
  const { track } = useAnalytics();

  const handleClick = () => {
    if (bookmark) {
      track({
        event: HallowAnalyticsEvent.BookmarkRemoved,
        properties: bibleProperties({ screen_name: "bible", ...bookmark }),
      });
      return deleteBookmark.mutateAsync({
        params: { path: { id: bookmark.id } },
      });
    } else {
      track({
        event: HallowAnalyticsEvent.BookmarkAdded,
        properties: bibleProperties({
          screen_name: "bible",
          book_id: book,
          chapter: parseInt(chapter.toString()),
          translation_id: translationId,
          translation_name: translation,
          type: "bookmark",
        }),
      });
      return addBookmark.mutateAsync({
        body: {
          translation_id: translationId,
          book_id: book,
          chapter: parseInt(chapter.toString()),
          title,
        },
      });
    }
  };

  return (
    <IconButton
      onClick={handleClick}
      variant="transparentPrimary"
      icon={!!bookmark ? <BookmarkIcon.Filled /> : <BookmarkIcon.Outline />}
    />
  );
};
